import {
  FC,
  useEffect,
  useState,
  KeyboardEventHandler,
  useRef,
  useCallback,
  useMemo,
} from "react";
import styled from "styled-components";

import { round } from "../utils";
import theme from "../theme";

const StyledInput = styled.input({
  display: "inline-flex",
  justifySelf: "flex-end",
  textAlign: "center",
  fontSize: theme.fontSize.default,
  backgroundColor: theme.color.gray,
  border: `2px solid ${theme.color.gray}`,
  padding: `${theme.space.tiny} ${theme.space.nano}`,
  borderRadius: theme.radius.medium,
  width: "auto",
  "&:focus": {
    border: `2px solid ${theme.color.lightBlack}`,
    outline: "none",
    background: "transparent",
  },
  "&::placeholder": {
    color: theme.color.darkGray,
  },
  "@media screen and (min-width: 800px)": {
    fontSize: theme.fontSize.medium,
  },
});

interface NumberInputProps {
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  integer?: boolean;
  size?: number;
}

const NumberInput: FC<NumberInputProps> = ({
  value,
  onChange,
  min,
  max,
  integer = false,
  ...rest
}) => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [commaPosition, setCommaPosition] = useState<number | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  const [wroteMinVal, setWroteMinVal] = useState<boolean>(false);
  const [caretPosition, setCaretPosition] = useState<number | null | undefined>(
    null
  );
  const valueLocaleString = useMemo(
    () => value.toLocaleString("da-DK"),
    [value]
  );
  const currentValue =
    valueLocaleString === `${min}`
      ? isActive
        ? wroteMinVal
          ? `${min}`
          : ""
        : `${min}`
      : valueLocaleString +
        (typeof commaPosition === "number" &&
        !valueLocaleString.includes(",") &&
        !integer
          ? ","
          : "");

  const updateCaret = useCallback(() => {
    setCaretPosition(ref.current?.selectionEnd);
  }, []);

  useEffect(() => {
    if (currentValue.length >= 2) {
      setWroteMinVal(false);
    }
  }, [currentValue]);

  useEffect(() => {
    const position = valueLocaleString.indexOf(",");
    setCommaPosition(position === -1 ? null : position);
  }, [valueLocaleString]);

  useEffect(() => {
    const currentRef = ref.current;
    if (currentRef) {
      currentRef.addEventListener("keypress", updateCaret);
      currentRef.addEventListener("keydown", updateCaret);
      currentRef.addEventListener("mousedown", updateCaret);
      currentRef.addEventListener("touchstart", updateCaret);
      currentRef.addEventListener("paste", updateCaret);
      currentRef.addEventListener("cut", updateCaret);
      currentRef.addEventListener("select", updateCaret);
      currentRef.addEventListener("selectstart", updateCaret);
    }
    return () => {
      if (currentRef) {
        currentRef.removeEventListener("keypress", updateCaret);
        currentRef.removeEventListener("keydown", updateCaret);
        currentRef.removeEventListener("mousedown", updateCaret);
        currentRef.removeEventListener("touchstart", updateCaret);
        currentRef.removeEventListener("paste", updateCaret);
        currentRef.removeEventListener("cut", updateCaret);
        currentRef.removeEventListener("select", updateCaret);
        currentRef.removeEventListener("selectstart", updateCaret);
      }
    };
  }, [updateCaret]);

  useEffect(() => {
    if (!isActive) {
      setCommaPosition(null);
    }
  }, [isActive]);

  const handleBlur = () => {
    setIsActive(false);
  };

  const handleFocus = () => {
    setIsActive(true);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (isActive) {
      if (e.key === ",") {
        setCommaPosition(
          typeof caretPosition === "number" && !currentValue.includes(",")
            ? caretPosition
            : null
        );
      }
      if (e.key === "Backspace" && commaPosition === (caretPosition ?? 0) - 1) {
        e.preventDefault();
        setCommaPosition(null);
      }
      if (e.key === "Backspace") {
        const lengthBefore = ref.current?.value.length ?? 0;
        const caretPositionBefore = ref.current?.selectionStart ?? 0;
        setTimeout(() => {
          const lengthAfter = ref.current?.value.length ?? 0;
          const caretPositionAfter = ref.current?.selectionStart ?? 0;
          const lengthDiff = lengthBefore - lengthAfter;
          if (caretPositionAfter > caretPositionBefore) {
            ref.current?.setSelectionRange(
              caretPositionBefore - lengthDiff,
              caretPositionBefore - lengthDiff
            );
          }
        }, 0);
      }
      if (e.key.match(/\d/)) {
        const lengthBefore = ref.current?.value.length ?? 0;
        const caretPositionBefore = ref.current?.selectionStart ?? 0;
        setTimeout(() => {
          const lengthAfter = ref.current?.value.length ?? 0;
          const caretPositionAfter = ref.current?.selectionStart ?? 0;
          const lengthDiff = lengthAfter - lengthBefore;
          if (caretPositionAfter >= caretPositionBefore) {
            ref.current?.setSelectionRange(
              caretPositionBefore + lengthDiff,
              caretPositionBefore + lengthDiff
            );
          }
        }, 0);
      }
      if (e.key === `${min}` && currentValue === "") {
        e.preventDefault();
        setWroteMinVal(true);
      }
    }
  };

  const handleNumberInputChange =
    (
      { integer, min, max }: { integer: boolean; min: number; max: number } = {
        integer: false,
        min: 0,
        max: 1,
      }
    ) =>
    (newValue: string) => {
      const removedDots = newValue.replaceAll(".", "");
      if (integer || !newValue.includes(",")) {
        const removedCommas = removedDots.replaceAll(",", "");
        const result = isNaN(parseInt(removedCommas))
          ? 0
          : parseInt(removedCommas);
        return Math.min(Math.max(result, min), max);
      } else {
        const short =
          removedDots.split(",")[0] +
          "," +
          removedDots.split(",")[1].slice(0, 2);
        const result = isNaN(parseFloat(short.replaceAll(",", ".")))
          ? 0
          : parseFloat(short.replaceAll(",", "."));
        return round(Math.min(Math.max(result, min), max));
      }
    };

  return (
    <StyledInput
      ref={ref}
      data-lpignore="true"
      value={currentValue}
      placeholder={min.toString()}
      onChange={(e) =>
        onChange(
          handleNumberInputChange({
            integer,
            min,
            max,
          })(e.target.value)
        )
      }
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      size={Math.max(max.toLocaleString("da-DK")?.length ?? 0, 2)}
      autoComplete="off"
      {...rest}
    />
  );
};

export default NumberInput;
